<script setup>
import { ref } from 'vue';

/*
En principio este componente es multiselect pero preconfigurado con algunos
valores por defecto, el css y las traducciones
*/
const props = defineProps({
  multiple: { type: Boolean, default: false },
  showClear: { type: Boolean, default: false },
  showLabels: { type: Boolean, default: false },
  // Si se asigna nombre al input, pondremos un input oculto con el valor seleccionado
  // Ojo que las opciones del select deben de tener el campo id que es lo que se
  // usa para obtener el valor
  inputName: { type: String, default: undefined },
});

const selector = ref();
const modelInterno = ref(props.multiple ? [] : null);

function clearAll() {
  selector.value.$emit('update:modelValue', props.multiple ? [] : null);
}
</script>

<template>
  <multiselect
    ref="selector"
    class="select-cifra"
    tag-placeholder="Pulsa enter para crear un elemento"
    :select-label="$I18n.t('pulse_enter_seleccionar')"
    :select-group-label="$I18n.t('pulse_enter_seleccionar_grupo')"
    :selected-label="$I18n.t('seleccionado')"
    :deselect-label="$I18n.t('pulse_enter_deseleccionar')"
    :deselect-group-label="$I18n.t('pulse_enter_deseleccionar_grupo')"
    :limit-text="(count) => $I18n.t('y_n_mas', { count })"
    :show-labels="showLabels"
    :multiple="multiple"
    v-bind="$attrs"
    @update:model-value="modelInterno = $event"
  >
    <!--
      Estos templates es para pasar los slots que nos pasan a nosotros
      al componente hijo
    -->
    <template v-for="(index, name) in $slots" #[name]="data">
      <slot :name="name" v-bind="data"></slot>
    </template>
    <template #clear>
      <a v-if="showClear" class="multiselect__clear" @click.prevent.stop="clearAll">
        <i class="fa fa-times"></i>
      </a>
    </template>
    <template #noOptions>
      {{ $I18n.t('No_hay_registros') }}
    </template>
    <template #noResult>
      {{ $I18n.t('No_hay_registros') }}
    </template>
  </multiselect>
  <template v-if="inputName !== undefined">
    <template v-if="multiple">
      <input
        v-for="opcion in modelInterno"
        :key="opcion.id"
        :name="`${inputName}[]`"
        type="hidden"
        :value="opcion.id"
      />
    </template>
    <template v-else>
      <input
        type="hidden"
        :name="inputName"
        :value="modelInterno?.id"
      >
    </template>
  </template>
</template>

<style lang="scss">
// OJO QUE NO ES SCOPED PORQUE QUEREMOS CREAR EL CSS GENERAL PARA QUE AFECTE A MULTISELECT!!
@import '@/../assets/stylesheets/cifra/vars/main.scss';
@import '@/../assets/stylesheets/cifra/mixins/iconos.scss';

$flecha-width: 24px;

.select-cifra.multiselect {
  min-height: 34px;

  &.multiselect--active {
    .multiselect__tags {
      border-color: $color-borde-input-focus;
    }
  }

  .multiselect__spinner {
    height: 32px;
  }

  .multiselect__input, .multiselect__single, .multiselect__placeholder {
    min-height: 30px;
    line-height: 30px;
    margin: 0;
    padding: 0;
  }
  &,
  .multiselect__input,
  .multiselect__single {
    color: $text-color;
    font-size: inherit;
    touch-action: manipulation;
  }

  .multiselect__option--selected {
    background-color: white;
    color: $text-color;
    font-weight: normal;

    &:before {
      @include icono-solid('\f14a');
      margin-right: 6px;
      color: $cifra-color;
    }

    &.multiselect__option--highlight {
      background: $color-danger;
      color: #fff;

      &:before {
        color: white;
      }
    }
  }

  .multiselect__option--highlight {
    background-color: $color-success;

    &::after {
      font-size: 0.8em;
    }
  }

  // Flecha del selector
  .multiselect__select {
    padding: 0;
    height: $control-height;
    width: $flecha-width;

    &:before {
      top: 60%;
    }
  }

  .multiselect__content-wrapper {
    border-color: $dark-gray;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  }

  .multiselect__tags {
    padding-top: 0;
    min-height: $control-height;
    margin-bottom: 0;
    border-radius: 1px;
    padding-right: $flecha-width;
    // Para que se vean dos líneas y media y salga el scroll
    max-height: 64px;
    overflow: auto;
  }

  .multiselect__tag {
    border-radius: 1px;
    margin-bottom: 0;
    margin-right: 2px;
    font-size: 0.8em;
    background-color: $light-gray;
    color: $text-color;

    &:first-child {
      margin-top: 6px;
    }

    &:last-child {
      margin-right: 0;
    }

    .multiselect__strong {
      margin-top: 5px;
    }

    .multiselect__tag-icon {
      &::after {
        color: $color-danger;
      }
      &:hover {
        background-color: $dark-gray;
      }
    }
  }

  .multiselect__clear {
    position: absolute;
    right: 25px;
    height: 40px;
    width: 25px;
    z-index: 9;
    line-height: 34px;
    text-align: center;

    ~ .multiselect__tags{
      padding-right: 60px;
    }

    .fa, .fas, .far {
      // Este es el color de la flecha, para que quede igual
      color: #999;
    }
  }

  .multiselect__option--group {
    font-weight: 600;
    text-align: center;
    background-color: $darkest-gray;
    color: white;
    &.multiselect__option--highlight {
      // background-color: $dark-gray;
      background-color: $color-success;

    }
  }
}
</style>
